/**
 * Funding tapes — `GET /fundings/tapes` + `GET /fundings/tapes/{id}/rows`
 * (funding-tape-prep.md §2, §4). One grid per tape whose columns come from the
 * tape SPEC (`fields[]`), each cell showing the populated value with its
 * provenance rung as a tooltip; a required-missing cell is highlighted the same
 * way the Excel pull does it. The action column reconciles (LOS vs documents),
 * moves, or removes the row. Status changes (open → locked → funded → archived)
 * and the xlsx pull live in the toolbar.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import CompareArrowsRounded from "@mui/icons-material/CompareArrowsRounded";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import DriveFileMoveOutlined from "@mui/icons-material/DriveFileMoveOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import { Muted, Pill } from "../../components/closingReadiness/ReadinessBits";
import { agreementMeta, formatCompactDollar } from "../../components/closingReadiness/closingReadinessUtils";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { TAPE_STATUSES, fundingsApi } from "../../api/fundingsApi";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import { ActionCell, actionColumn } from "../admin/shared/adminTableActions";
import { amber, slate, status, steel } from "../../theme/colors";
import TapeReconcileDrawer from "./TapeReconcileDrawer";
import { C, CenterCell, MONO, StatCard, loanHref, pct } from "./fundingsUi";

const STATUS_TONE = {
  open: { c: status.success.fg, bg: status.success.bg },
  locked: { c: steel.main, bg: steel.tint },
  funded: { c: slate[600], bg: slate[100] },
  archived: { c: slate[500], bg: slate[100] },
};

// Render a populated cell by the column's field name: dollars for amount-ish
// fields, dates for date-ish ones, everything else as-is.
const cellText = (field, v) => {
  if (v == null || v === "") return "";
  if (/amount|price|balance|holdback|proceeds|fee/i.test(field) && typeof v === "number") return formatDollar(v);
  if (/date/i.test(field) && typeof v === "string") return formatDate(v, v);
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : String(v);
  return String(v);
};

const FundingTapesView = ({ tapes, tapesLoading, onTapesChanged }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [tapeId, setTapeId] = useState("");
  const [rowsData, setRowsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includeExited, setIncludeExited] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reconcile, setReconcile] = useState(null); // row
  const [moveMenu, setMoveMenu] = useState(null); // { anchor, row }

  useEffect(() => {
    if (!tapeId && Array.isArray(tapes) && tapes.length) {
      setTapeId(tapes.find((t) => t.status === "open")?.id || tapes[0].id);
    }
  }, [tapes, tapeId]);

  const tape = useMemo(() => (tapes || []).find((t) => t.id === tapeId) || rowsData?.tape || null, [tapes, tapeId, rowsData]);

  const load = useCallback(async () => {
    if (!tapeId) return;
    setLoading(true);
    try {
      setRowsData(await fundingsApi.getRows(tapeId, { includeExited }));
    } catch (err) {
      console.error("tape rows error:", err);
      setRowsData(null);
    } finally {
      setLoading(false);
    }
  }, [tapeId, includeExited]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => (Array.isArray(rowsData?.rows) ? rowsData.rows : []), [rowsData]);

  const download = async () => {
    if (!tapeId) return;
    setDownloading(true);
    try {
      const blob = await fundingsApi.getXlsx(tapeId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tapeId}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.detail || "Download failed.", { variant: "error" });
    } finally {
      setDownloading(false);
    }
  };

  const setStatus = async (next) => {
    if (!tapeId || !next || next === tape?.status) return;
    const fundingDate = next === "funded" ? new Date().toISOString().slice(0, 10) : undefined;
    try {
      await fundingsApi.setStatus(tapeId, { status: next, fundingDate });
      enqueueSnackbar(`${tape?.name || tapeId} is now ${next}.`, { variant: "success" });
      onTapesChanged?.();
      load();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.detail || "Could not change the tape status.", { variant: "error" });
    }
  };

  const remove = async (row) => {
    if (!row?.dealId) return;
    try {
      await fundingsApi.removeLoan(tapeId, row.dealId);
      enqueueSnackbar(`Removed #${row.loanNumber ?? row.dealId} from ${tapeId}.`, { variant: "success" });
      onTapesChanged?.();
      load();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.detail || "Could not remove the loan.", { variant: "error" });
    }
  };

  const move = async (toTapeId) => {
    const row = moveMenu?.row;
    setMoveMenu(null);
    if (!row?.dealId || !toTapeId) return;
    try {
      const res = await fundingsApi.moveLoan(tapeId, row.dealId, toTapeId);
      const missing = Array.isArray(res?.missingRequired) ? res.missingRequired : [];
      enqueueSnackbar(
        `Moved #${row.loanNumber ?? row.dealId} to ${toTapeId}.${missing.length ? ` Missing there: ${missing.join(", ")}.` : ""}`,
        { variant: missing.length ? "warning" : "success" },
      );
      onTapesChanged?.();
      load();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.detail || "Could not move the loan.", { variant: "error" });
    }
  };

  // Spec columns + the trailing record columns the xlsx also carries.
  const columnDefs = useMemo(() => {
    const spec = Array.isArray(tape?.fields) ? tape.fields : [];
    const specCols = spec.map((f) => ({
      headerName: f.label || f.field,
      colId: `f:${f.field}`,
      minWidth: 110,
      flex: /name|address|originator|los/i.test(f.field) ? 1.4 : 1,
      valueGetter: (p) => p.data?.values?.[f.field]?.value,
      cellRenderer: (p) => {
        const cell = p.data?.values?.[f.field] || {};
        const missing = !!cell.missing && (cell.required || f.required);
        const a = cell.agreement;
        const am = a ? agreementMeta(a.status) : null;
        return (
          <Tooltip
            title={
              <Box sx={{ fontSize: 11.5, whiteSpace: "pre-line" }}>
                {[
                  `source: ${cell.source || "—"}`,
                  cell.resolvedAt ? `resolved ${formatDateTime(cell.resolvedAt, "datetime")}` : null,
                  a ? `${am.label}: LOS ${a.losValue ?? "—"} · doc ${a.documentValue ?? "—"} (${a.documentSource || "no read"})` : null,
                ]
                  .filter(Boolean)
                  .join("\n")}
              </Box>
            }
            arrow
            enterDelay={500}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                height: "100%",
                width: "100%",
                minWidth: 0,
                px: missing ? 0.75 : 0,
                mx: missing ? -0.75 : 0,
                background: missing ? status.error.bg : "transparent",
                color: missing ? status.error.fg : C.ink,
                fontFamily: /amount|rate|date|number|id|months|term|ltv|pct/i.test(f.field) ? MONO : undefined,
                fontSize: 12.5,
              }}
            >
              <Box sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {missing ? "missing" : cellText(f.field, p.value)}
              </Box>
              {a && a.status !== "agree" && (
                <Box sx={{ width: 7, height: 7, borderRadius: "99px", background: am.c, flexShrink: 0 }} />
              )}
            </Box>
          </Tooltip>
        );
      },
    }));

    return [
      {
        headerName: "Loan",
        colId: "loan",
        pinned: "left",
        minWidth: 190,
        valueGetter: (p) => `${p.data?.loanNumber ?? ""} ${p.data?.loanName ?? ""}`,
        cellRenderer: (p) => (
          <CenterCell left>
            <Box sx={{ minWidth: 0 }}>
              <Txt sx={{ fontSize: 12.5, fontWeight: 700, color: C.ink, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {p.data?.loanNumber != null ? `#${p.data.loanNumber}` : p.data?.dealId}
                {p.data?.loanName ? ` · ${p.data.loanName}` : ""}
              </Txt>
              <Txt sx={{ fontFamily: MONO, fontSize: 10.5, color: C.faint, display: "block" }}>
                {[p.data?.originatingLos, p.data?.status, p.data?.exitedAt ? "exited" : null].filter(Boolean).join(" · ")}
              </Txt>
            </Box>
          </CenterCell>
        ),
      },
      ...specCols,
      {
        headerName: "Missing",
        colId: "missing",
        width: 110,
        valueGetter: (p) => (p.data?.missingRequired || []).length,
        cellRenderer: (p) => (
          <CenterCell>
            <Pill
              label={p.value ? `${p.value} required` : "complete"}
              c={p.value ? status.error.fg : status.success.fg}
              bg={p.value ? status.error.bg : status.success.bg}
              size="xs"
            />
          </CenterCell>
        ),
      },
      {
        headerName: "Pull-through",
        colId: "pull",
        width: 105,
        valueGetter: (p) => p.data?.pullThroughProbability,
        cellRenderer: (p) => {
          const v = Number(p.value ?? 0);
          const tone = v >= 0.75 ? status.success : v >= 0.4 ? { fg: amber.chipText, bg: amber.light } : status.error;
          return (
            <CenterCell>
              <Pill label={pct(p.value)} c={tone.fg} bg={tone.bg} size="xs" />
            </CenterCell>
          );
        },
      },
      {
        headerName: "Projection",
        colId: "projection",
        width: 150,
        headerTooltip: "Days off first / last projection once funded; expected-date changes · windows seen",
        valueGetter: (p) => p.data?.projection?.expectedDateChangeCount ?? 0,
        cellRenderer: (p) => {
          const pr = p.data?.projection || {};
          return (
            <CenterCell>
              <Txt sx={{ fontFamily: MONO, fontSize: 11.5, color: C.sub }}>
                {pr.actualFundedDate
                  ? `${pr.daysOffFirst ?? "—"}d / ${pr.daysOffLast ?? "—"}d`
                  : `${pr.expectedDateChangeCount ?? 0} moves · ${pr.timesInScope ?? 0}×`}
              </Txt>
            </CenterCell>
          );
        },
      },
      {
        headerName: "Diffs",
        colId: "diffs",
        width: 80,
        valueGetter: (p) => (p.data?.diffs || []).length,
        cellRenderer: (p) => (
          <CenterCell>
            <Pill label={String(p.value)} c={p.value ? amber.chipText : slate[500]} bg={p.value ? amber.light : slate[100]} size="xs" />
          </CenterCell>
        ),
      },
      actionColumn({
        colId: "actions",
        count: 4,
        cellRenderer: (p) => {
          const href = loanHref(p.data);
          const locked = tape?.status && tape.status !== "open";
          return (
            <ActionCell>
              <Tooltip title="Reconcile LOS vs documents">
                <IconButton size="small" onClick={() => setReconcile(p.data)}>
                  <CompareArrowsRounded fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={href ? "Open loan" : "Lives in the other LOS"}>
                <span>
                  <IconButton size="small" disabled={!href} onClick={() => href && navigate(href)}>
                    <OpenInNewRounded fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={locked ? "Tape is not open" : "Move to another tape"}>
                <span>
                  <IconButton size="small" disabled={locked} onClick={(e) => setMoveMenu({ anchor: e.currentTarget, row: p.data })}>
                    <DriveFileMoveOutlined fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={locked ? "Tape is not open" : "Remove from tape"}>
                <span>
                  <IconButton size="small" disabled={locked} onClick={() => remove(p.data)}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </ActionCell>
          );
        },
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tape, navigate, tapeId]);

  const totals = useMemo(() => {
    const amountField = (tape?.fields || []).find((f) => /fundingAmount|totalLoanAmount|baseLoanAmount/i.test(f.field))?.field;
    const amount = amountField ? rows.reduce((s, r) => s + (Number(r?.values?.[amountField]?.value) || 0), 0) : null;
    const missing = rows.filter((r) => (r?.missingRequired || []).length).length;
    const disagree = rows.filter((r) => Object.values(r?.values || {}).some((v) => v?.agreement && ["disagree", "doc_only"].includes(v.agreement.status))).length;
    return { amount, missing, disagree };
  }, [rows, tape]);

  const st = STATUS_TONE[tape?.status] || STATUS_TONE.open;

  return (
    <Box>
      <Flx ac jb wrap g={1.5} sx={{ mb: 2 }}>
        <Flx ac wrap g={1.5}>
          <TextField
            select
            size="small"
            value={tapeId}
            onChange={(e) => setTapeId(e.target.value)}
            sx={{ minWidth: 220, background: C.surface, ".MuiInputBase-root": { fontSize: 13 } }}
          >
            {(tapes || []).map((t) => (
              <MenuItem key={t.id} value={t.id} sx={{ fontSize: 13 }}>
                {t.name || t.id} · {t.status}
              </MenuItem>
            ))}
          </TextField>
          {tape && <Pill label={tape.status} c={st.c} bg={st.bg} />}
          {tape?.fundingDate && <Muted>funding {formatDate(tape.fundingDate)}</Muted>}
          {tape?.autoScope && <Pill label="auto-scope" c={steel.main} bg={steel.tint} size="xs" title="The forward book populates this tape on its own" />}
          <FormControlLabel
            control={<Switch size="small" checked={includeExited} onChange={(e) => setIncludeExited(e.target.checked)} />}
            label={<Txt sx={{ fontSize: 12.5, color: C.sub }}>Include exited</Txt>}
            sx={{ ml: 0.5 }}
          />
          {(loading || tapesLoading) && <CircularProgress size={16} sx={{ color: C.faint }} />}
        </Flx>
        <Flx ac g={1}>
          <TextField
            select
            size="small"
            value={tape?.status || "open"}
            onChange={(e) => setStatus(e.target.value)}
            disabled={!tape}
            sx={{ minWidth: 130, background: C.surface, ".MuiInputBase-root": { fontSize: 13 } }}
          >
            {TAPE_STATUSES.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>
                Mark {s}
              </MenuItem>
            ))}
          </TextField>
          <Button
            size="small"
            variant="contained"
            startIcon={downloading ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : <FileDownloadOutlined />}
            onClick={download}
            disabled={!tapeId || downloading}
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            Excel
          </Button>
        </Flx>
      </Flx>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 2 }}>
        <StatCard label="Loans on tape" value={rowsData?.count ?? rows.length} sub={tape?.fundedLoanIds?.length ? `${tape.fundedLoanIds.length} funded` : `${(tape?.pipelines || []).join(" + ") || "both pipelines"}`} />
        <StatCard label="Tape amount" value={totals.amount == null ? "—" : formatCompactDollar(totals.amount) || "$0"} sub={totals.amount == null ? "no amount column" : formatDollar(totals.amount)} />
        <StatCard label="Missing required" value={totals.missing} alert={totals.missing > 0} sub="rows with a required gap" />
        <StatCard label="Disagreements" value={totals.disagree} alert={totals.disagree > 0} sub="rows where a document differs from the LOS" />
      </Box>

      <AgGridTableWrapper height="auto">
        <AgGridReact
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={{
            resizable: true,
            sortable: true,
            suppressHeaderMenuButton: true,
            minWidth: 80,
            cellStyle: { display: "flex", alignItems: "center" },
          }}
          getRowId={(p) => p.data?.id || `${p.data?.originatingLos}:${p.data?.dealId}`}
          domLayout="autoHeight"
          pagination
          paginationPageSize={25}
          rowHeight={50}
          headerHeight={38}
          suppressCellFocus
          overlayNoRowsTemplate={
            loading ? "<span></span>" : "<span style='font-size:12.5px;color:#94a3b8'>No loans on this tape — add them from the forward view</span>"
          }
        />
      </AgGridTableWrapper>

      <Menu open={Boolean(moveMenu)} anchorEl={moveMenu?.anchor} onClose={() => setMoveMenu(null)}>
        {(tapes || [])
          .filter((t) => t.id !== tapeId && t.status === "open")
          .map((t) => (
            <MenuItem key={t.id} onClick={() => move(t.id)} sx={{ fontSize: 13 }}>
              {t.name || t.id}
            </MenuItem>
          ))}
        {(tapes || []).filter((t) => t.id !== tapeId && t.status === "open").length === 0 && (
          <MenuItem disabled sx={{ fontSize: 13 }}>
            No other open tape
          </MenuItem>
        )}
      </Menu>

      <TapeReconcileDrawer
        tapeId={tapeId}
        row={reconcile}
        open={Boolean(reconcile)}
        onClose={() => setReconcile(null)}
        onChanged={load}
      />
    </Box>
  );
};

export default FundingTapesView;
